// React Query hooks for dashboard functionality
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { createDashboardService } from './dashboardService'

// Query keys for React Query - include userId to prevent cache sharing between users
export const dashboardKeys = {
  all: (userId) => ['dashboard', userId],
  data: (userId) => [...dashboardKeys.all(userId), 'data'],
  apiKeys: (userId) => [...dashboardKeys.all(userId), 'apiKeys'],
  billing: (userId) => [...dashboardKeys.all(userId), 'billing'],
}

// Custom hook to get dashboard data
export const useDashboardData = (getAuthToken, userId, onUnauthorized = null, options = {}) => {
  return useQuery({
    queryKey: dashboardKeys.data(userId),
    queryFn: async () => {
      const dashboardService = createDashboardService(getAuthToken, onUnauthorized)
      return await dashboardService.getDashboardData()
    },
    enabled: typeof getAuthToken === 'function' && !!getAuthToken() && !!userId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: (failureCount, error) => {
      // Don't retry on 401 errors (already handled)
      if (error?.status === 401) return false
      return failureCount < 3
    },
    ...options,
  })
}

// Custom hook to get API keys
export const useApiKeys = (getAuthToken, userId, onUnauthorized = null, options = {}) => {
  return useQuery({
    queryKey: dashboardKeys.apiKeys(userId),
    queryFn: async () => {
      const dashboardService = createDashboardService(getAuthToken, onUnauthorized)
      const response = await dashboardService.getApiKeys()
      // The API returns the keys in the response.apiKeys array
      return response
    },
    enabled: !!getAuthToken && !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error) => {
      // Don't retry on 401 errors (already handled)
      if (error?.status === 401) return false
      return failureCount < 3
    },
    ...options,
  })
}

// Custom hook to create API key
export const useCreateApiKey = (getAuthToken, userId, onUnauthorized = null) => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (keyName) => {
      const dashboardService = createDashboardService(getAuthToken, onUnauthorized)
      return await dashboardService.createApiKey(keyName)
    },
    onSuccess: () => {
      // Invalidate and refetch API keys and dashboard data
      queryClient.invalidateQueries({ queryKey: dashboardKeys.apiKeys(userId) })
      queryClient.invalidateQueries({ queryKey: dashboardKeys.data(userId) })
    },
    onError: (error) => {
      console.error('Failed to create API key:', error)
    },
  })
}

// Custom hook to revoke API key
export const useRevokeApiKey = (getAuthToken, userId, onUnauthorized = null) => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (keyId) => {
      const dashboardService = createDashboardService(getAuthToken, onUnauthorized)
      return await dashboardService.revokeApiKey(keyId)
    },
    onSuccess: () => {
      // Invalidate and refetch API keys and dashboard data
      queryClient.invalidateQueries({ queryKey: dashboardKeys.apiKeys(userId) })
      queryClient.invalidateQueries({ queryKey: dashboardKeys.data(userId) })
    },
    onError: (error) => {
      console.error('Failed to revoke API key:', error)
    },
  })
}

// Custom hook to generate icons
export const useGenerateIcons = (getAuthToken, onUnauthorized = null) => {
  return useMutation({
    mutationFn: async (iconData) => {
      const dashboardService = createDashboardService(getAuthToken, onUnauthorized)
      return await dashboardService.generateIcons(iconData)
    },
    onError: (error) => {
      console.error('Failed to generate icons:', error)
    },
  })
}

// Custom hook to get billing data
export const useBillingData = (getAuthToken, userId, onUnauthorized = null, options = {}) => {
  return useQuery({
    queryKey: dashboardKeys.billing(userId),
    queryFn: async () => {
      const dashboardService = createDashboardService(getAuthToken, onUnauthorized)
      return await dashboardService.getBilling()
    },
    enabled: typeof getAuthToken === 'function' && !!getAuthToken() && !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error) => {
      if (error?.status === 401) return false
      return failureCount < 3
    },
    ...options,
  })
}

// Custom hook to create Stripe Customer Portal session
export const useStripePortalSession = (getAuthToken, onUnauthorized = null) => {
  return useMutation({
    mutationFn: async () => {
      const dashboardService = createDashboardService(getAuthToken, onUnauthorized)
      return await dashboardService.createStripePortalSession()
    },
    onError: (error) => {
      console.error('Failed to create Stripe portal session:', error)
    },
  })
}

// Custom hook to delete user account
export const useDeleteAccount = (getAuthToken, onUnauthorized = null) => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (captchaToken) => {
      const dashboardService = createDashboardService(getAuthToken, onUnauthorized)
      return await dashboardService.deleteAccount(captchaToken)
    },
    onSuccess: () => {
      // Clear all cached data since account is being deleted
      queryClient.clear()
    },
    onError: (error) => {
      console.error('Failed to delete account:', error)
    },
  })
}
